'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import SiteHeader from '@/components/SiteHeader';
import SiteFooter from '@/components/SiteFooter';
import { Eye, MapPin, Shield, Star, Home, Users, Briefcase, ArrowRight, AlertCircle, Loader2, Building2, Plane } from 'lucide-react';

type PageState = 'loading' | 'populated' | 'unavailable';

const standoutReasons = [
  {
    id: 'architecture',
    icon: Building2,
    title: 'Iconic Architecture',
    body: "Designed by Klai Juba Wald and recognized with an AIA Merit Award, Panorama's twin aqua-blue glass towers are among the most architecturally distinctive residential buildings in Las Vegas. This is not a generic high-rise.",
    accent: '#4CC9FF',
  },
  {
    id: 'privacy', icon: Shield, title: 'Privacy and Controlled Access', body: "A staffed guard gate, valet-supported entry, and a resident-first culture create a living environment that feels genuinely private — without feeling isolated.", accent: '#7A5CFF',
  },
  {
    id: 'location',icon: MapPin,title: 'One Block from the Strip',body: "At 4525 and 4575 Dean Martin Drive, Panorama sits at the intersection of urban convenience and residential calm. You're minutes from everything Las Vegas offers — without living in the middle of it.",
    accent: '#FF3DF5',
  },
  {
    id: 'views',
    icon: Eye,
    title: 'Views That Earn Their Keep',
    body: "Las Vegas skyline on one side. Red Rock Canyon on the other. From the upper floors, the views are a daily reminder of why you chose this address.",
    accent: '#4CC9FF',
  },
  {
    id: 'amenities',
    icon: Star,
    title: 'Amenities That Match the Standard',
    body: "Dual pools, fitness centers, spa facilities, massage rooms, concierge service, and valet — the amenity package at Panorama is designed for residents who expect more.",
    accent: '#7A5CFF',
  },
  {
    id: 'community',
    icon: Users,
    title: 'A Real Community',
    body: "Panorama has a genuine resident culture — neighbors who know each other, plan outings together, and look out for one another. The private community platform exists because residents wanted it.",
    accent: '#FF3DF5',
  },
];

const lockAndLeavePoints = [
  'Valet-supported arrivals and departures',
  'Concierge team handles packages and guest coordination',
  'Guarded entry provides peace of mind when you\'re away',
  'Building staff familiar with second-home resident patterns',
  'Community network keeps you connected even from a distance',
  'Low-maintenance lifestyle compared to a standalone property',
];

const residentTypes = [
  {
    id: 'owners',
    icon: Home,
    title: 'Owners',
    description: 'Panorama ownership offers long-term value in one of Las Vegas\'s most recognized residential addresses. Whether you\'re full-time or part-time, the building delivers.',
    accent: '#4CC9FF',
  },
  {
    id: 'renters',
    icon: Briefcase,
    title: 'Renters',
    description: 'Renting at Panorama means access to the full amenity experience and a genuine community — not just a unit in a building. Renters are full members of the Panorama community.',
    accent: '#7A5CFF',
  },
  {
    id: 'second-home',
    icon: Plane,
    title: 'Second-Home Residents',
    description: 'Panorama is purpose-built for the lock-and-leave lifestyle. Many residents use it as a Las Vegas base — for business, entertainment, or simply having a premium address in the city.',
    accent: '#FF3DF5',
  },
];

export default function WhyPanoramaPage() {
  const [pageState, setPageState] = useState<PageState>('loading');

  useEffect(() => {
    const t = setTimeout(() => setPageState('populated'), 800);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="min-h-screen" style={{ background: '#07060C' }}>
      <SiteHeader theme="transparent" />
      <main>
        {/* Hero */}
        <section className="relative pt-32 pb-20 overflow-hidden panorama-hero-bg">
          <div
            className="absolute top-0 right-0 w-[60%] h-[75%] pointer-events-none"
            style={{ background: 'radial-gradient(ellipse 70% 65% at 100% 5%, rgba(255,61,245,0.18) 0%, rgba(122,92,255,0.08) 50%, transparent 70%)' }}
          />
          <div
            className="absolute bottom-0 left-0 w-[45%] h-[50%] pointer-events-none"
            style={{ background: 'radial-gradient(ellipse 80% 70% at 0% 100%, rgba(76,201,255,0.12) 0%, transparent 65%)' }}
          />
          <div className="relative z-10 max-w-screen-2xl mx-auto px-6 lg:px-10">
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 mb-6"
              style={{ borderRadius: '6px', background: 'rgba(122,92,255,0.10)', border: '1px solid rgba(122,92,255,0.22)' }}
            >
              <div className="w-1.5 h-1.5 rounded-full" style={{ background: '#7A5CFF' }} />
              <span className="text-[12px] font-medium tracking-wide" style={{ color: '#7A5CFF' }}>
                Why Panorama
              </span>
            </div>
            <h1
              className="font-display font-extrabold text-[48px] sm:text-[60px] lg:text-[68px] leading-[1.04] tracking-tight mb-5 max-w-3xl"
              style={{ color: '#F2F4FF' }}
            >
              There is no other{' '}
              <span className="text-transparent bg-clip-text" style={{ backgroundImage: 'linear-gradient(135deg, #FF3DF5 0%, #7A5CFF 50%, #4CC9FF 100%)' }}>
                address like this
              </span>{' '}
              in Las Vegas.
            </h1>
            <p className="text-[18px] leading-relaxed max-w-2xl mb-10 font-light" style={{ color: 'rgba(242,244,255,0.72)' }}>
              Two award-winning towers. A genuine resident community. Amenities that match the lifestyle. And a location that puts all of Las Vegas within reach — without putting you in the middle of it.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link href="/request-access" className="btn-primary group">
                Request Access
                <ArrowRight size={15} className="transition-transform duration-200 group-hover:translate-x-0.5" />
              </Link>
              <Link href="/amenities" className="btn-secondary">
                Explore Amenities
              </Link>
            </div>
          </div>
          <div className="absolute bottom-0 left-0 right-0 neon-divider" />
        </section>

        {/* Loading */}
        {pageState === 'loading' && (
          <section className="py-24" style={{ background: '#0C0A16' }}>
            <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 flex flex-col items-center justify-center py-20 gap-4">
              <Loader2 size={32} className="animate-spin" style={{ color: '#7A5CFF' }} />
              <p className="text-[15px]" style={{ color: '#A7A3C4' }}>Loading…</p>
            </div>
          </section>
        )}

        {/* Unavailable */}
        {pageState === 'unavailable' && (
          <section className="py-24" style={{ background: '#0C0A16' }}>
            <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 flex flex-col items-center justify-center py-20 gap-4 text-center">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: 'rgba(255,61,245,0.10)', border: '1px solid rgba(255,61,245,0.20)' }}>
                <AlertCircle size={22} style={{ color: '#FF3DF5' }} />
              </div>
              <h3 className="font-display font-semibold text-[20px]" style={{ color: '#F2F4FF' }}>Content unavailable</h3>
              <p className="text-[14px] max-w-sm" style={{ color: '#A7A3C4' }}>We're having trouble loading this page. Please try again shortly.</p>
              <Link href="/" className="btn-secondary text-[13px] px-4 py-2 mt-2">Return Home</Link>
            </div>
          </section>
        )}

        {/* Populated */}
        {pageState === 'populated' && (
          <>
            {/* Why it stands out */}
            <section className="py-20" style={{ background: '#0C0A16' }}>
              <div className="max-w-screen-2xl mx-auto px-6 lg:px-10">
                <div className="text-center mb-14">
                  <p className="text-[12px] font-semibold tracking-[0.12em] uppercase mb-3" style={{ color: '#4CC9FF' }}>
                    What sets Panorama apart
                  </p>
                  <h2 className="font-display font-bold text-[36px] sm:text-[42px] leading-tight tracking-tight mb-4" style={{ color: '#F2F4FF' }}>
                    Six reasons residents choose Panorama.
                  </h2>
                  <p className="text-[16px] max-w-xl mx-auto" style={{ color: '#A7A3C4' }}>
                    Not every high-rise earns its reputation. Panorama has.
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {standoutReasons.map((r) => (
                    <div key={r.id} className="neon-card p-7">
                      <div
                        className="w-10 h-10 rounded-xl flex items-center justify-center mb-5"
                        style={{ background: `${r.accent}12`, border: `1px solid ${r.accent}22` }}
                      >
                        <r.icon size={18} style={{ color: r.accent }} />
                      </div>
                      <h3 className="font-display font-semibold text-[17px] mb-3" style={{ color: '#F2F4FF' }}>
                        {r.title}
                      </h3>
                      <p className="text-[13.5px] leading-relaxed" style={{ color: '#A7A3C4' }}>
                        {r.body}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Lock and leave */}
            <section className="py-20" style={{ background: '#07060C' }}>
              <div className="neon-divider mb-0" />
              <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 pt-20">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                  <div>
                    <p className="text-[12px] font-semibold tracking-[0.12em] uppercase mb-3" style={{ color: '#FF3DF5' }}>
                      Lock-and-Leave Living
                    </p>
                    <h2 className="font-display font-bold text-[36px] sm:text-[42px] leading-tight tracking-tight mb-5" style={{ color: '#F2F4FF' }}>
                      Leave when you want. Come back to exactly what you left.
                    </h2>
                    <p className="text-[16px] leading-relaxed mb-6" style={{ color: 'rgba(167,163,196,0.90)' }}>
                      Panorama is designed for residents who travel, work remotely, or maintain multiple homes. The building's staffing, services, and community culture make it genuinely easy to be away — and genuinely good to return.
                    </p>
                    <p className="text-[15px] leading-relaxed mb-8" style={{ color: 'rgba(167,163,196,0.75)' }}>
                      Many Panorama residents use it as a Las Vegas base — for business, entertainment, or simply having a premium address in the city. The lock-and-leave lifestyle is not an afterthought here. It's a feature.
                    </p>
                    <ul className="space-y-3">
                      {lockAndLeavePoints.map((point, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <div className="w-5 h-5 rounded flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: 'rgba(255,61,245,0.10)', border: '1px solid rgba(255,61,245,0.20)' }}>
                            <div className="w-1.5 h-1.5 rounded-full" style={{ background: '#FF3DF5' }} />
                          </div>
                          <span className="text-[14px] leading-relaxed" style={{ color: 'rgba(167,163,196,0.85)' }}>{point}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  {/* Image placeholder */}
                  <div
                    className="relative overflow-hidden h-[420px] flex flex-col items-center justify-center"
                    style={{ borderRadius: '12px', border: '1px solid rgba(255,255,255,0.08)', background: 'linear-gradient(135deg, rgba(255,61,245,0.06) 0%, rgba(122,92,255,0.08) 50%, rgba(76,201,255,0.05) 100%)' }}
                  >
                    <div className="absolute top-1/4 right-1/4 w-48 h-48 rounded-full blur-3xl" style={{ background: 'rgba(255,61,245,0.08)' }} />
                    <div className="absolute top-1/3 left-1/3 w-36 h-36 rounded-full blur-2xl" style={{ background: 'rgba(76,201,255,0.06)' }} />
                    <div className="relative z-10 text-center px-8">
                      <div
                        className="w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4"
                        style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)' }}
                      >
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="rgba(167,163,196,0.30)" strokeWidth="1.5">
                          <rect x="3" y="3" width="18" height="18" rx="2" />
                          <circle cx="8.5" cy="8.5" r="1.5" />
                          <path d="m21 15-5-5L5 21" />
                        </svg>
                      </div>
                      <p className="text-[13px] font-medium mb-1" style={{ color: 'rgba(167,163,196,0.40)' }}>Photography coming soon</p>
                      <p className="text-[11px]" style={{ color: 'rgba(167,163,196,0.25)' }}>Panorama Towers exterior & lifestyle</p>
                    </div>
                    <div className="absolute bottom-6 left-6 right-6">
                      <div className="glass-card-dark p-5" style={{ borderRadius: '10px' }}>
                        <p className="text-[13px] font-medium mb-2" style={{ color: '#4CC9FF' }}>Second-Home Advantage</p>
                        <p className="text-[14px] leading-relaxed" style={{ color: 'rgba(167,163,196,0.85)' }}>
                          "Panorama makes it easy to have a Las Vegas home without the overhead of managing one. The building handles the rest."
                        </p>
                        <p className="text-[12px] mt-3" style={{ color: 'rgba(167,163,196,0.45)' }}>— Resident perspective</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Who lives here */}
            <section className="py-20" style={{ background: '#0C0A16' }}>
              <div className="neon-divider mb-0" />
              <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 pt-20">
                <div className="text-center mb-12">
                  <p className="text-[12px] font-semibold tracking-[0.12em] uppercase mb-3" style={{ color: '#7A5CFF' }}>
                    Who calls Panorama home
                  </p>
                  <h2 className="font-display font-bold text-[36px] sm:text-[42px] leading-tight tracking-tight mb-4" style={{ color: '#F2F4FF' }}>
                    Panorama works for different kinds of residents.
                  </h2>
                  <p className="text-[16px] max-w-xl mx-auto" style={{ color: '#A7A3C4' }}>
                    Owners, renters, and second-home residents all find something distinct here.
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  {residentTypes.map((rt) => (
                    <div key={rt.id} className="neon-card p-7">
                      <div
                        className="w-10 h-10 rounded-xl flex items-center justify-center mb-5"
                        style={{ background: `${rt.accent}12`, border: `1px solid ${rt.accent}22` }}
                      >
                        <rt.icon size={18} style={{ color: rt.accent }} />
                      </div>
                      <h3 className="font-display font-semibold text-[17px] mb-3" style={{ color: '#F2F4FF' }}>
                        {rt.title}
                      </h3>
                      <p className="text-[13.5px] leading-relaxed" style={{ color: '#A7A3C4' }}>
                        {rt.description}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Location advantage */}
            <section className="py-20" style={{ background: '#07060C' }}>
              <div className="neon-divider mb-0" />
              <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 pt-20">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                  <div
                    className="relative overflow-hidden panorama-premium-bg h-[380px] flex items-center justify-center"
                    style={{ borderRadius: '12px', border: '1px solid rgba(255,255,255,0.08)' }}
                  >
                    <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse 80% 70% at 50% 50%, rgba(76,201,255,0.08) 0%, transparent 70%)' }} />
                    <div className="relative z-10 text-center p-8">
                      <div className="font-display font-bold text-[48px] mb-2" style={{ color: '#4CC9FF' }}>~1 mi</div>
                      <div className="text-[16px] font-medium mb-4" style={{ color: '#F2F4FF' }}>from the Las Vegas Strip</div>
                      <div className="grid grid-cols-2 gap-4 mt-6">
                        {[
                          { label: 'Harry Reid Airport', value: '~10 min' },
                          { label: 'Convention Center', value: '~8 min' },
                          { label: 'T-Mobile Arena', value: '~5 min' },
                          { label: 'The Sphere', value: '~12 min' },
                        ].map((item) => (
                          <div key={item.label} className="glass-card-dark p-3 text-center" style={{ borderRadius: '8px' }}>
                            <div className="font-semibold text-[14px]" style={{ color: '#F2F4FF' }}>{item.value}</div>
                            <div className="text-[11px] mt-0.5" style={{ color: '#A7A3C4' }}>{item.label}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div>
                    <p className="text-[12px] font-semibold tracking-[0.12em] uppercase mb-3" style={{ color: '#4CC9FF' }}>
                      Location Advantage
                    </p>
                    <h2 className="font-display font-bold text-[36px] sm:text-[42px] leading-tight tracking-tight mb-5" style={{ color: '#F2F4FF' }}>
                      Everything Las Vegas offers. None of the noise.
                    </h2>
                    <p className="text-[16px] leading-relaxed mb-5" style={{ color: 'rgba(167,163,196,0.90)' }}>
                      Panorama's address on Dean Martin Drive puts you within minutes of the Strip, the airport, the convention center, and every major entertainment venue in the city — while sitting just far enough away to feel like a real home.
                    </p>
                    <p className="text-[15px] leading-relaxed mb-8" style={{ color: 'rgba(167,163,196,0.75)' }}>
                      For business travelers, entertainers, and second-home owners, the location is a genuine competitive advantage. You're never far from where you need to be.
                    </p>
                    <div className="flex flex-wrap gap-3">
                      <Link href="/nearby" className="btn-secondary">
                        Explore the Neighborhood
                      </Link>
                      <Link href="/lifestyle" className="btn-secondary">
                        See the Lifestyle
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* CTA */}
            <section className="py-20" style={{ background: '#0C0A16' }}>
              <div className="neon-divider mb-0" />
              <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 pt-20 text-center">
                <div
                  className="relative overflow-hidden panorama-premium-bg rounded-xl py-16 px-8 sm:px-14"
                  style={{ border: '1px solid rgba(255,255,255,0.08)' }}
                >
                  <div className="absolute top-0 right-0 w-[45%] h-full pointer-events-none" style={{ background: 'radial-gradient(ellipse 80% 70% at 100% 10%, rgba(255,61,245,0.14) 0%, transparent 65%)' }} />
                  <div className="absolute bottom-0 left-0 w-[40%] h-[60%] pointer-events-none" style={{ background: 'radial-gradient(ellipse 90% 80% at 0% 100%, rgba(76,201,255,0.10) 0%, transparent 65%)' }} />
                  <div className="relative z-10 max-w-xl mx-auto">
                    <h2 className="font-display font-bold text-[32px] sm:text-[40px] leading-tight tracking-tight mb-4" style={{ color: '#F2F4FF' }}>
                      Thinking of living at Panorama?
                    </h2>
                    <p className="text-[16px] mb-8" style={{ color: 'rgba(167,163,196,0.85)' }}>
                      Explore real estate opportunities or request access to the private resident community platform.
                    </p>
                    <div className="flex flex-wrap items-center justify-center gap-4">
                      <Link href="/request-access" className="btn-primary group">
                        Request Access
                        <ArrowRight size={15} className="transition-transform duration-200 group-hover:translate-x-0.5" />
                      </Link>
                      <Link href="/real-estate" className="btn-secondary">
                        View Real Estate
                      </Link>
                    </div>
                  </div>
                </div>

                {/* Cross-links */}
                <div
                  className="mt-14 pt-10 border-t"
                  style={{ borderColor: 'rgba(255,255,255,0.06)' }}
                >
                  <p className="text-[11px] font-semibold tracking-[0.10em] uppercase mb-5" style={{ color: 'rgba(167,163,196,0.35)' }}>
                    Explore more
                  </p>
                  <div className="flex flex-wrap items-center justify-center gap-3">
                    <Link href="/amenities" className="text-[13px] font-medium px-4 py-2 rounded-lg transition-all duration-150 hover:bg-white/[0.06]" style={{ color: '#A7A3C4', border: '1px solid rgba(255,255,255,0.08)' }}>
                      Amenities
                    </Link>
                    <Link href="/lifestyle" className="text-[13px] font-medium px-4 py-2 rounded-lg transition-all duration-150 hover:bg-white/[0.06]" style={{ color: '#A7A3C4', border: '1px solid rgba(255,255,255,0.08)' }}>
                      The Lifestyle
                    </Link>
                    <Link href="/community" className="text-[13px] font-medium px-4 py-2 rounded-lg transition-all duration-150 hover:bg-white/[0.06]" style={{ color: '#A7A3C4', border: '1px solid rgba(255,255,255,0.08)' }}>
                      The Community
                    </Link>
                    <Link href="/nearby" className="text-[13px] font-medium px-4 py-2 rounded-lg transition-all duration-150 hover:bg-white/[0.06]" style={{ color: '#A7A3C4', border: '1px solid rgba(255,255,255,0.08)' }}>
                      What's Nearby
                    </Link>
                    <Link href="/faq" className="text-[13px] font-medium px-4 py-2 rounded-lg transition-all duration-150 hover:bg-white/[0.06]" style={{ color: '#A7A3C4', border: '1px solid rgba(255,255,255,0.08)' }}>
                      FAQ
                    </Link>
                  </div>
                </div>
              </div>
            </section>
          </>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
